// apps/client/src/pages/conversations/ConversationThreadPage.tsx
//
// Single-thread view. Top: participant + assignment card. Middle:
// message list ordered ascending (oldest → newest, autoscroll bottom
// on new). Bottom: ConversationComposer with all the 24h/flag gates.
//
// We subscribe to the parent conversation doc directly via a single-doc
// onSnapshot so the composer's 24h gate updates the instant a fresh
// inbound message arrives (canonical plan N1: lastInboundCustomerMessageAt
// is rewritten on every inbound, which slides the 24h window forward).
//
// Messages are merged from two sources:
//   1. Firestore subscription (useConversationMessages) — source of truth
//      for sent/delivered docs.
//   2. Optimistic local state — pending outbound docs the composer
//      appended before the callable returned. Once the real doc lands
//      via Firestore, we drop the matching local:* bubble.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  MessageSquare,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import {
  doc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { useMessengerDmMode } from '../../hooks/useMessengerDmMode';
import {
  MessageDoc,
  useConversationMessages,
} from '../../hooks/useConversationMessages';
import type { ConversationDoc } from '../../hooks/useConversations';
import ConversationComposer from './ConversationComposer';

type OutletCtx = {
  userData?: {
    uid?: string;
    companyId?: string;
    role?: string;
    displayName?: string;
  };
};

interface ThreadConversation extends Omit<ConversationDoc, 'id'> {
  id: string;
}

function formatTime(ts: Timestamp | null): string {
  if (!ts) return '';
  try {
    return ts.toDate().toLocaleString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '';
  }
}

function participantLabel(p: ThreadConversation['participantProfile']): string {
  const composed = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return composed || 'Unknown participant';
}

const ConversationThreadPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const companyId = userData?.companyId ?? '';
  const currentUid = userData?.uid;
  const currentRole = userData?.role;

  const [conversation, setConversation] = useState<ThreadConversation | null>(
    null
  );
  const [conversationLoading, setConversationLoading] = useState(true);
  const [conversationError, setConversationError] = useState<string | null>(
    null
  );

  // Single-doc subscription — composer's 24h gate must react to every
  // new inbound message, which only updates lastInboundCustomerMessageAt
  // when the conversation doc itself flips.
  useEffect(() => {
    if (!companyId || !conversationId) {
      setConversationLoading(false);
      return;
    }
    setConversationLoading(true);
    setConversationError(null);

    const ref = doc(
      db,
      `companies/${companyId}/conversations/${conversationId}`
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setConversation(null);
          setConversationError('Conversation not found.');
          setConversationLoading(false);
          return;
        }
        const data = snap.data() as Partial<ConversationDoc>;
        setConversation({
          id: snap.id,
          source: data.source ?? 'meta_messenger',
          pageId: data.pageId ?? '',
          pageName: data.pageName,
          psid: data.psid ?? '',
          participantProfile: data.participantProfile ?? {
            firstName: null,
            lastName: null,
            profilePicUrl: null,
            hydratedAt: null,
          },
          createdAt: data.createdAt ?? null,
          lastMessageAt: data.lastMessageAt ?? null,
          lastMessageDirection: data.lastMessageDirection ?? null,
          lastMessagePreview: data.lastMessagePreview ?? null,
          unreadCount:
            typeof data.unreadCount === 'number' ? data.unreadCount : 0,
          assignedBrokerId: data.assignedBrokerId ?? null,
          lastInboundCustomerMessageAt:
            data.lastInboundCustomerMessageAt ?? null,
          windowExpiresAt: data.windowExpiresAt ?? null,
          status: (data.status as ConversationDoc['status']) ?? 'open',
          linkedLeadId: data.linkedLeadId ?? null,
          capturedAt: data.capturedAt ?? null,
        });
        setConversationLoading(false);
      },
      (err) => {
        console.error('[ConversationThreadPage] doc subscription failed', err);
        setConversationError(err instanceof Error ? err.message : String(err));
        setConversationLoading(false);
      }
    );
    return () => unsub();
  }, [companyId, conversationId]);

  const { messages, loading: messagesLoading, error: messagesError } =
    useConversationMessages(companyId, conversationId);

  // Optimistic outbound messages keyed by local mid. Dropped when the
  // real doc arrives (matched by mid prefix) or when the composer
  // explicitly calls rollback.
  const [optimistic, setOptimistic] = useState<MessageDoc[]>([]);

  const onOptimistic = useCallback((msg: MessageDoc) => {
    setOptimistic((prev) => [...prev, msg]);
  }, []);

  const onOptimisticRollback = useCallback((mid: string) => {
    setOptimistic((prev) => prev.filter((m) => m.mid !== mid));
  }, []);

  // When the real outbound doc lands, drop matching optimistic bubbles.
  // Heuristic: a real outbound message exists in messages[] with the
  // same senderBrokerId, no `local:` prefix, AND its timestamp is at or
  // after the optimistic doc's timestamp AND the text matches. That's
  // sufficient correlation for v1; the alternative (server returns the
  // pre-swap localMid back to the client) is a tighter loop but adds
  // an extra round trip for marginal gain.
  useEffect(() => {
    if (optimistic.length === 0) return;
    const realOutbound = messages.filter(
      (m) =>
        m.direction === 'outbound' &&
        !m.mid.startsWith('local:') &&
        m.sendStatus !== 'pending'
    );
    if (realOutbound.length === 0) return;
    setOptimistic((prev) =>
      prev.filter((opt) => {
        return !realOutbound.some(
          (real) =>
            real.text === opt.text &&
            real.senderBrokerId === opt.senderBrokerId &&
            real.timestamp &&
            opt.timestamp &&
            real.timestamp.toMillis() >= opt.timestamp.toMillis() - 1000
        );
      })
    );
  }, [messages, optimistic.length]);

  const mergedMessages = useMemo(() => {
    const merged = [...messages, ...optimistic];
    merged.sort((a, b) => {
      const aMs = a.timestamp?.toMillis() ?? 0;
      const bMs = b.timestamp?.toMillis() ?? 0;
      return aMs - bMs;
    });
    return merged;
  }, [messages, optimistic]);

  // Autoscroll bottom on new message.
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [mergedMessages.length]);

  // Reads the authoritative subcollection flag doc (same doc the backend
  // webhook + sendOutboundMessage read) — NOT the company-doc featureFlags
  // map, which is a different Firestore location never written for this flag.
  const featureFlagMode = useMessengerDmMode(companyId);

  if (conversationLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={MessageSquare}
          title="Conversation"
          subtitle="Loading thread…"
          isRefreshing
        />
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading conversation…
        </div>
      </div>
    );
  }

  if (conversationError || !conversation) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={MessageSquare}
          title="Conversation"
          subtitle="Thread not available"
        />
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {conversationError ?? 'Conversation not found.'}
        </div>
        <button
          type="button"
          onClick={() => navigate('/leads/dm-inbox')}
          className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-white"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to inbox
        </button>
      </div>
    );
  }

  const customer = participantLabel(conversation.participantProfile);

  return (
    <div className="flex flex-col h-full space-y-4">
      <PageHeader
        icon={MessageSquare}
        title={customer}
        subtitle={
          conversation.pageName
            ? `via ${conversation.pageName}`
            : `via Page ${conversation.pageId}`
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/leads/dm-inbox')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-600/50 text-gray-300 hover:text-white hover:bg-white/[0.04] text-sm"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span>Inbox</span>
          </button>
        }
      />

      {/* Participant + assignment card */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 grid gap-3 sm:grid-cols-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Customer
          </div>
          <div className="mt-1 text-sm text-white">{customer}</div>
          <div className="mt-0.5 text-[11px] text-gray-500">
            PSID ******{conversation.psid.slice(-6)}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Assigned broker
          </div>
          <div className="mt-1 text-sm text-white">
            {conversation.assignedBrokerId ? (
              conversation.assignedBrokerId === currentUid ? (
                <span className="inline-flex items-center gap-1 text-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  You
                </span>
              ) : (
                conversation.assignedBrokerId
              )
            ) : (
              <span className="text-amber-400">Unassigned</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500">
            Status
          </div>
          <div className="mt-1 text-sm text-white capitalize">
            {conversation.status}
          </div>
          {conversation.windowExpiresAt && (
            <div className="mt-0.5 text-[11px] text-gray-500 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Window closes {formatTime(conversation.windowExpiresAt)}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 rounded-lg border border-gray-700 bg-gray-900/30 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messagesError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-xs flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{messagesError}</span>
            </div>
          )}
          {messagesLoading && mergedMessages.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-gray-400 text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
              Loading messages…
            </div>
          ) : mergedMessages.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-10">
              No messages yet.
            </div>
          ) : (
            mergedMessages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                currentUid={currentUid}
                customerLabel={customer}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <ConversationComposer
          companyId={companyId}
          conversationId={conversation.id}
          assignedBrokerId={conversation.assignedBrokerId}
          windowExpiresAt={conversation.windowExpiresAt}
          lastInboundCustomerMessageAt={
            conversation.lastInboundCustomerMessageAt
          }
          featureFlagMode={featureFlagMode}
          currentUid={currentUid}
          currentRole={currentRole}
          onOptimistic={onOptimistic}
          onOptimisticRollback={onOptimisticRollback}
        />
      </div>
    </div>
  );
};

const MessageBubble: React.FC<{
  message: MessageDoc;
  currentUid: string | undefined;
  customerLabel: string;
}> = ({ message, currentUid, customerLabel }) => {
  const isOutbound = message.direction === 'outbound';
  const isOwnSend = isOutbound && message.senderBrokerId === currentUid;
  const align = isOutbound ? 'items-end' : 'items-start';
  const bubbleTone = isOutbound
    ? 'bg-blue-600/20 border-blue-500/40 text-white'
    : 'bg-gray-800 border-gray-700 text-gray-100';
  const senderLabel = isOutbound
    ? isOwnSend
      ? 'You'
      : 'Broker'
    : customerLabel;
  const pending =
    message.sendStatus === 'pending' || message.mid.startsWith('local:');
  const failed = message.sendStatus === 'failed';
  const unconfirmed = message.sendStatus === 'sent_unconfirmed';

  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
        {senderLabel} · {formatTime(message.timestamp)}
        {pending && <span className="ml-2 text-amber-300">sending…</span>}
        {failed && <span className="ml-2 text-red-300">failed</span>}
        {unconfirmed && (
          <span className="ml-2 text-amber-300">sent, syncing</span>
        )}
      </div>
      <div
        className={`max-w-[80%] rounded-lg border px-3 py-2 text-sm whitespace-pre-wrap break-words ${bubbleTone} ${
          pending ? 'opacity-70' : ''
        }`}
      >
        {message.text ?? (
          <span className="italic text-gray-400">[non-text message]</span>
        )}
        {message.attachments.length > 0 && (
          <div className="mt-1 text-[11px] text-gray-400">
            {message.attachments.length} attachment
            {message.attachments.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
      {failed && message.sendError && (
        <div className="text-[10px] text-red-300 mt-1">{message.sendError}</div>
      )}
    </div>
  );
};

export default ConversationThreadPage;
